import { useEffect, useState } from 'react';
import { Button, Label, FormGroup, FormFeedback, Input } from 'reactstrap';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, Controller } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import Icon from '@mdi/react';
import { mdiEye, mdiEyeClosed, mdiInformation } from '@mdi/js';
import loginLogo from '../../assets/images/loginLogo.png';
import loginBanner from '../../assets/images/loginBanner.png';
import './Login.scss';
import { authenticateUser } from '../../store/apis/userService';
import { initialValues, validationSchema } from './constants';
import { encode } from '../../utils/encrypt.util';
import { handlePostLogin } from '../../utils/auth.util';
import { withBase } from '../../utils/route.util';

function Login() {
  document.title = 'Login | Envision - Daikin';
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    localStorage.removeItem('2FAUserId');
    localStorage.removeItem('2FAEmail');
    localStorage.removeItem('2FAMobile');
  }, []);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const {
    mutate: login,
    isPending,
    isError,
    error,
  } = useMutation({ mutationFn: authenticateUser });

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(validationSchema),
    defaultValues: initialValues,
  });

  const onSubmit = form => {
    const formData = {
      username: form.username,
      password: encode(form.password),
    };

    login(formData, {
      onSuccess: data => {
        if (data?.twoFactorRequired) {
          localStorage.setItem('2FAUserId', data.userId);
          localStorage.setItem('2FAEmail', data.email);
          localStorage.setItem('2FAMobile', data.mobile);
          if (form?.rememberMe) {
            window.location.href = withBase('login/2FA?r=true');
          } else {
            window.location.href = withBase('login/2FA');
          }
        } else {
          // Use the utility function to handle post-login actions
          handlePostLogin(data, '/dashboard', form?.rememberMe);
        }
      },
    });
  };

  return (
    <div className="login-container">
      {/* Left Panel */}
      <div className="login-left slide-in-left">
        <img src={loginBanner} alt="Daikin Banner" className="banner" />
      </div>

      {/* Right Panel */}
      <div className="login-right slide-in-right">
        <img
          src={loginLogo}
          alt="Daikin 100th Anniversary Logo"
          className="top-logo"
          style={{ width: '60%' }}
        />
        <div className="form-wrapper">
          <h2 className="form-title fs-1">Welcome Back !</h2>
          <p className="form-subtitle fs-5">
            Please Enter Your Login Credentials
          </p>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Label
              for="username"
              className="d-flex flex-row align-items-center gap-1"
            >
              <div>
                <span>User ID</span>
                <span className="text-danger">*</span>
              </div>
              <Icon
                path={mdiInformation}
                size={0.65}
                color="#017EBD"
                title="Enter your employee User ID"
              />
            </Label>
            <Controller
              name="username"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  id="username"
                  type="text"
                  placeholder="Enter User ID"
                  className={`form-control external-filter-input ${errors.username ? 'is-invalid' : ''}`}
                  title="Enter your employee User ID"
                />
              )}
            />
            {errors.username && (
              <FormFeedback>{errors.username.message}</FormFeedback>
            )}
            <Label
              for="password"
              className="mt-3 d-flex flex-row align-items-center gap-1"
            >
              <div>
                <span>Password</span>
                <span className="text-danger">*</span>
              </div>
              <Icon
                path={mdiInformation}
                size={0.65}
                color="#017EBD"
                title="Enter your login password"
              />
            </Label>
            <Controller
              name="password"
              control={control}
              render={({ field }) => (
                <div style={{ position: 'relative' }}>
                  <Input
                    {...field}
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter Password"
                    className={`form-control external-filter-input ${errors.password ? 'is-invalid' : ''}`}
                    title="Enter your login password"
                    style={{
                      position: 'relative',
                      paddingRight: '.5rem',
                    }}
                  />
                  {!errors.password && (
                    <button
                      className="non-btn"
                      style={{
                        position: 'absolute',
                        right: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                      }}
                      type="button"
                      onClick={togglePasswordVisibility}
                    >
                      <Icon
                        path={showPassword ? mdiEyeClosed : mdiEye}
                        size={0.7}
                        color="#4D4D4D"
                      />
                    </button>
                  )}
                  {errors.password && (
                    <FormFeedback>{errors.password.message}</FormFeedback>
                  )}
                </div>
              )}
            />
            <FormGroup switch className="mt-4 mb-5">
              <Controller
                name="rememberMe"
                control={control}
                render={({ field }) => (
                  <div className="d-flex flex-row align-items-center gap-2">
                    <Input
                      {...field}
                      type="switch"
                      role="switch"
                      id="rememberMe"
                      name="rememberMe"
                      className="custom-switch-size my-0"
                      checked={field.value}
                    />
                    <Label for="rememberMe" className="remember-label my-0">
                      Remember Me
                    </Label>
                  </div>
                )}
              />
            </FormGroup>
            <Button
              className="w-100 mt-5 custom-btn"
              type="submit"
              disabled={isPending}
            >
              {isPending ? 'Logging in...' : 'Login'}
            </Button>
          </form>
          {isError && (
            <p className="text-danger my-1">{error.message ?? error.error}</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default Login;
